"use client";

import { useEditor, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import ListKeymap from "@tiptap/extension-list-keymap";
import Typography from "@tiptap/extension-typography";
import CharacterCount from "@tiptap/extension-character-count";
import { Markdown } from "@tiptap/markdown";
import { useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import { cn } from "~/lib/utils";
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  ListChecks,
  List,
  ListOrdered,
  Heading2,
} from "lucide-react";
import type { Editor } from "@tiptap/core";

interface EditorToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
}

const EditorToolbarButton = ({
  onClick,
  isActive = false,
  disabled = false,
  children,
  title,
}: EditorToolbarButtonProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded transition-colors",
        "hover:bg-accent/80 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-1",
        "disabled:pointer-events-none disabled:opacity-40",
        isActive
          ? "bg-accent text-accent-foreground"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
};

export interface LinkPreviewData {
  title?: string;
  description?: string;
  image?: string;
  url: string;
}

export type MarkdownEditorHandle = {
  getEditor: () => Editor | null;
  getTaskItems: () => Array<{ content: string; checked: boolean }>;
  setTaskListContent: (
    items: Array<{ content: string; checked: boolean }>,
  ) => void;
  focus: () => void;
  getCharacterCount: () => number;
};

type MarkdownEditorProps = {
  content?: string;
  placeholder?: string;
  className?: string;
  onChange?: (markdown: string) => void;
  onUrlDetected?: (url: string, metadata?: LinkPreviewData) => void;
  onTaskListDetected?: () => void;
  editable?: boolean;
  autoFocus?: boolean;
  taskListOnly?: boolean;
  showBubbleMenu?: boolean;
  maxCharacters?: number;
};

export const MarkdownEditor = forwardRef<
  MarkdownEditorHandle,
  MarkdownEditorProps
>(function MarkdownEditor(
  {
    content = "",
    placeholder = "Start typing...",
    className,
    onChange,
    onUrlDetected,
    onTaskListDetected,
    editable = true,
    autoFocus = false,
    taskListOnly = false,
    showBubbleMenu = true,
    maxCharacters,
  },
  ref,
) {
  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: taskListOnly ? false : { levels: [1, 2, 3] },
          bulletList: taskListOnly ? false : undefined,
          orderedList: taskListOnly ? false : undefined,
          blockquote: taskListOnly ? false : undefined,
          codeBlock: taskListOnly ? false : undefined,
          horizontalRule: taskListOnly ? false : undefined,
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
        }),
        TaskList,
        TaskItem.configure({
          nested: true,
        }),
        Placeholder.configure({
          placeholder,
        }),
        ListKeymap,
        Typography,
        CharacterCount.configure({
          ...(maxCharacters ? { limit: maxCharacters } : {}),
        }),
        Markdown,
      ],
      content: taskListOnly
        ? content ||
          '<ul data-type="taskList"><li data-type="taskItem" data-checked="false"></li></ul>'
        : content,
      editable,
      autofocus: autoFocus,
      editorProps: {
        attributes: {
          class: cn(
            "prose prose-sm dark:prose-invert max-w-none focus:outline-none",
            "prose-headings:font-semibold prose-headings:tracking-tight",
            "prose-a:text-primary prose-a:no-underline hover:prose-a:underline",
            "prose-code:text-xs prose-code:bg-muted prose-code:px-1 prose-code:rounded prose-code:before:content-none prose-code:after:content-none",
            "prose-pre:bg-muted prose-pre:border",
            "prose-ul:my-2 prose-ol:my-2",
            "prose-li:my-0",
            "min-h-[2.5rem] px-3 py-2",
            "[&_ul[data-type='taskList']]:list-none [&_ul[data-type='taskList']]:pl-0",
            "[&_li[data-type='taskItem']]:flex [&_li[data-type='taskItem']]:items-start [&_li[data-type='taskItem']]:gap-2",
            "[&_li[data-type='taskItem'][data-checked='true']>div]:line-through [&_li[data-type='taskItem'][data-checked='true']>div]:opacity-50",
            "[&_li[data-type='taskItem']>label]:mt-0.5",
            "[&_li[data-type='taskItem']>label_input]:rounded [&_li[data-type='taskItem']>label_input]:border-border",
          ),
        },
        handlePaste: (_view, event) => {
          const text = event.clipboardData?.getData("text/plain");
          if (text && onUrlDetected) {
            const urlRegex = /(https?:\/\/[^\s]+)/g;
            const urls = text.match(urlRegex);
            if (urls && urls.length > 0) {
              urls.forEach((url) => {
                setTimeout(() => onUrlDetected(url), 0);
              });
            }
          }
          return false;
        },
        handleDrop: (_view, event) => {
          const text = event.dataTransfer?.getData("text/plain");
          if (text && onUrlDetected) {
            const urlRegex = /(https?:\/\/[^\s]+)/g;
            const urls = text.match(urlRegex);
            if (urls && urls.length > 0) {
              urls.forEach((url) => {
                setTimeout(() => onUrlDetected(url), 0);
              });
            }
          }
          return false;
        },
      },
      onUpdate: ({ editor: ed }) => {
        if (!onChange) return;

        try {
          const markdown = ed.getMarkdown();
          onChange(markdown);
        } catch (error) {
          console.error("Markdown serialization error:", error);
          const text = ed.getText();
          onChange(text);
        }

        if (onTaskListDetected && !taskListOnly) {
          const hasTaskList = ed
            .getJSON()
            .content?.some((node) => node.type === "taskList");
          if (hasTaskList) {
            onTaskListDetected();
          }
        }
      },
      onBlur: ({ editor: ed }) => {
        if (!onChange) return;

        try {
          const markdown = ed.getMarkdown();
          onChange(markdown);
        } catch (error) {
          console.error("Markdown serialization error:", error);
          const text = ed.getText();
          onChange(text);
        }
      },
    },
    [],
  );

  const getTaskItems = useCallback(() => {
    if (!editor) return [];
    const items: Array<{ content: string; checked: boolean }> = [];
    const json = editor.getJSON();
    const extractTasks = (nodes: any[] | undefined) => {
      if (!nodes) return;
      for (const node of nodes) {
        if (node.type === "taskItem") {
          const text =
            node.content
              ?.map((child: any) => child.text || "")
              .join("")
              .trim() || "";
          items.push({ content: text, checked: node.attrs?.checked ?? false });
        }
        if (node.content) {
          extractTasks(node.content);
        }
      }
    };
    extractTasks(json.content as any[]);
    return items;
  }, [editor]);

  const setTaskListContent = useCallback(
    (items: Array<{ content: string; checked: boolean }>) => {
      if (!editor) return;
      const taskItems = items.map((item) => ({
        type: "taskItem" as const,
        attrs: { checked: item.checked },
        content: item.content
          ? [
              {
                type: "paragraph" as const,
                content: [{ type: "text" as const, text: item.content }],
              },
            ]
          : [{ type: "paragraph" as const }],
      }));
      if (taskItems.length === 0) {
        taskItems.push({
          type: "taskItem" as const,
          attrs: { checked: false },
          content: [{ type: "paragraph" as const }],
        });
      }
      editor
        .chain()
        .setContent({
          type: "doc",
          content: [{ type: "taskList", content: taskItems }],
        })
        .focus("end")
        .run();
    },
    [editor],
  );

  useImperativeHandle(
    ref,
    () => ({
      getEditor: () => editor,
      getTaskItems,
      setTaskListContent,
      focus: () => editor?.commands.focus(),
      getCharacterCount: () =>
        editor?.storage.characterCount?.characters() ?? 0,
    }),
    [editor, getTaskItems, setTaskListContent],
  );

  useEffect(() => {
    if (!editor) return;

    const currentMarkdown = editor.getMarkdown?.() || editor.getText();

    if (currentMarkdown !== content) {
      editor.commands.setContent(content || "");
    }
  }, [content, editor]);

  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        event.key === "l"
      ) {
        event.preventDefault();
        editor.chain().focus().toggleTaskList().run();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [editor]);

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={cn("w-full", className)}>
      {showBubbleMenu && !taskListOnly && (
        <BubbleMenu
          editor={editor}
          options={{
            placement: "top",
          }}
          className={cn(
            "flex items-center gap-0.5 rounded-lg border bg-popover p-1 shadow-md",
            "animate-in fade-in-0 zoom-in-95",
          )}
        >
          <EditorToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive("bold")}
            title="Bold (Cmd+B)"
          >
            <Bold className="h-4 w-4" />
          </EditorToolbarButton>

          <EditorToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive("italic")}
            title="Italic (Cmd+I)"
          >
            <Italic className="h-4 w-4" />
          </EditorToolbarButton>

          <EditorToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive("strike")}
            title="Strikethrough"
          >
            <Strikethrough className="h-4 w-4" />
          </EditorToolbarButton>

          <EditorToolbarButton
            onClick={() => editor.chain().focus().toggleCode().run()}
            isActive={editor.isActive("code")}
            title="Inline Code"
          >
            <Code className="h-4 w-4" />
          </EditorToolbarButton>

          <div className="mx-1 h-5 w-px bg-border" />

          <EditorToolbarButton
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            isActive={editor.isActive("heading", { level: 2 })}
            title="Heading"
          >
            <Heading2 className="h-4 w-4" />
          </EditorToolbarButton>

          <EditorToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive("bulletList")}
            title="Bullet List"
          >
            <List className="h-4 w-4" />
          </EditorToolbarButton>

          <EditorToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive("orderedList")}
            title="Numbered List"
          >
            <ListOrdered className="h-4 w-4" />
          </EditorToolbarButton>

          <EditorToolbarButton
            onClick={() => editor.chain().focus().toggleTaskList().run()}
            isActive={editor.isActive("taskList")}
            title="Checklist (Cmd+Shift+L)"
          >
            <ListChecks className="h-4 w-4" />
          </EditorToolbarButton>
        </BubbleMenu>
      )}

      <EditorContent editor={editor} />
    </div>
  );
});
